import { z } from 'zod';
import {
  AttachmentSchema,
  ConversationIdSchema,
  MessageIdSchema,
} from '../conversation/message_envelope';
import { MessageAuthorSchema } from '../conversation/message_author';
import { EnvelopeOrgIdSchema } from '../conversation/org_id';
import { DeskAgentProfileSchema } from './desk_session';

/**
 * Commands and facts exchanged between `conversation` and `sunshine-integration`
 * on the `desk-events` SNS topic (RawMessageDelivery, filtered by the `eventType`
 * attribute = `type`). The bridge puts a Zendesk agent in front of an app
 * traveler: conversation owns the state, sunshine owns every Sunco call.
 */
export const DESK_EVENT_TYPES = Object.freeze({
  open: 'desk.open',
  relay: 'desk.relay',
  close: 'desk.close',
  agentReply: 'desk.agent_reply',
  openFailed: 'desk.open_failed',
  released: 'desk.released',
  opened: 'desk.opened',
} as const);

export const HumanDeskSchema = z.literal('zendesk');
export type HumanDesk = z.infer<typeof HumanDeskSchema>;

export const DeskOpenCommandSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.open),
    orgId: EnvelopeOrgIdSchema,
    conversationId: ConversationIdSchema,
    traveler: z
      .object({
        externalId: z.string().min(1),
        name: z.string().min(1).optional(),
        // Contact used ONLY to find the traveler's legacy Zendesk customer
        // (sunshine stamps external_id on it). PII — never logged.
        email: z.email().optional(),
        phone: z.string().min(1).optional(),
        // The traveler's company (VOLL companyId + display name) — sunshine
        // stamps it on the ticket (tag + optional fields) so the CS routes by
        // company in Zendesk. Not PII. Rolling deploy: sunshine must parse it
        // before conversation sends it (`traveler` is strict).
        company: z
          .object({ id: z.string().min(1), name: z.string().min(1).optional() })
          .strict()
          .optional(),
      })
      .strict(),
    summary: z.string().max(2000).optional(),
    recentMessages: z
      .array(
        z.object({ author: MessageAuthorSchema, text: z.string(), at: z.iso.datetime() }).strict(),
      )
      .max(20),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type DeskOpenCommand = z.infer<typeof DeskOpenCommandSchema>;

export const DeskRelayCommandSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.relay),
    orgId: EnvelopeOrgIdSchema,
    conversationId: ConversationIdSchema,
    messageId: MessageIdSchema,
    text: z.string().min(1).nullable(),
    hasAttachment: z.boolean(),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type DeskRelayCommand = z.infer<typeof DeskRelayCommandSchema>;

/**
 * The traveler closed a desk-owned conversation in the app: sunshine posts a
 * note to the ticket, releases control in Sunco and marks the bridge released.
 * `openedAt` is the claim being closed (conversation's `deskOpenedAt`); absent
 * for a pre-claim-id desk conversation.
 */
export const DeskCloseCommandSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.close),
    orgId: EnvelopeOrgIdSchema,
    conversationId: ConversationIdSchema,
    openedAt: z.iso.datetime().optional(),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type DeskCloseCommand = z.infer<typeof DeskCloseCommandSchema>;

export const DeskAgentReplyEventSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.agentReply),
    conversationId: ConversationIdSchema,
    messageId: MessageIdSchema,
    text: z.string().min(1),
    // Files the agent sent (Sunco `mediaUrl`); `text` still carries the caption
    // or a fallback notice for readers that render no attachment. Optional on
    // the wire for the rolling deploy: this schema is strict, so conversation
    // ships BEFORE a sunshine that sends it.
    attachments: z.array(AttachmentSchema).max(20).optional(),
    // Who answered (first name + avatar). Optional on the wire for the rolling
    // deploy: this schema is strict, so conversation ships BEFORE a sunshine
    // that sends it.
    agent: DeskAgentProfileSchema.optional(),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type DeskAgentReplyEvent = z.infer<typeof DeskAgentReplyEventSchema>;

export const DeskOpenFailedEventSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.openFailed),
    conversationId: ConversationIdSchema,
    reason: z.enum(['terminal', 'attempts_exhausted', 'dry_run', 'desk_busy']),
    occurredAt: z.iso.datetime(),
    // The `occurredAt` of the `desk.open` that opened this bridge — the claim
    // id. Echoed back by sunshine so conversation can tell a stale/duplicate
    // release apart from the bridge it currently owns. Optional on the wire
    // for the rolling deploy: absent `openedAt` keeps the current behavior.
    openedAt: z.iso.datetime().optional(),
  })
  .strict();
export type DeskOpenFailedEvent = z.infer<typeof DeskOpenFailedEventSchema>;

/** Zendesk agent closed or solved the ticket; hand the conversation back to the AI. */
export const DeskReleasedEventSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.released),
    conversationId: ConversationIdSchema,
    reason: z.enum(['ticketClosed', 'transferToEmail', 'ticketSolved', 'other']),
    occurredAt: z.iso.datetime(),
    // The `occurredAt` of the `desk.open` that opened this bridge — the claim
    // id. Echoed back by sunshine so conversation can tell a stale/duplicate
    // release apart from the bridge it currently owns. Optional on the wire
    // for the rolling deploy: absent `openedAt` keeps the current behavior.
    openedAt: z.iso.datetime().optional(),
  })
  .strict();
export type DeskReleasedEvent = z.infer<typeof DeskReleasedEventSchema>;

/**
 * Sunshine publishes this after `passControl` reached the Agent Workspace
 * (`markWithAgent`): the human desk is now live for the conversation.
 * `openedAt` is the claim id (the conversation's `deskOpenedAt`).
 */
export const DeskOpenedEventSchema = z
  .object({
    type: z.literal(DESK_EVENT_TYPES.opened),
    conversationId: ConversationIdSchema,
    openedAt: z.iso.datetime(),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type DeskOpenedEvent = z.infer<typeof DeskOpenedEventSchema>;
